import React from 'react'
import styled from 'styled-components'

import { StringUtil } from '@root/shared'

import type { ShortcutRouteHandler } from '../shortcut-router'

const intersperse = <T, S>(
  array: Array<T>,
  separator: (prev: T, index: number) => S,
) => {
  return array.flatMap((item, i) => {
    if (i === array.length - 1) {
      return [item]
    }
    return [item, separator(item, i)]
  })
}

export const resolveCommandShow = (
  commandShow: ShortcutRouteHandler['commandShow'],
): ReadonlyArray<string> => {
  if ('mac' in commandShow) {
    const isMac =
      typeof navigator !== 'undefined' && /mac/i.test(navigator.platform)

    return isMac ? commandShow.mac : commandShow.windows
  }

  return commandShow
}

const makeComponentFromCommandShow = (commandShow: ReadonlyArray<string>) => {
  const keys = commandShow.map((key) => {
    const arr = key
      .replaceAll(/\s/gi, '')
      .split('+')
      .flatMap((key, i) => (
        <Key key={`${key}-${i}`}>
          <KeyInner>{StringUtil.capitalize(key)}</KeyInner>
        </Key>
      ))

    return (
      <KeySequence key={key}>
        {intersperse(arr, (_prev, i) => (
          <KeySequenceConnector key={`${i}+`}>+</KeySequenceConnector>
        ))}
      </KeySequence>
    )
  })

  return (
    <KeySequences key={commandShow.join(',')}>
      {intersperse(keys, (_prev, i) => (
        <KeySequencesSeparator key={`${i},`}>/</KeySequencesSeparator>
      ))}
    </KeySequences>
  )
}

export interface ShortcutCommandShowProps {
  commandShow: ShortcutRouteHandler['commandShow']
}

export const ShortcutCommandShow = ({
  commandShow,
}: ShortcutCommandShowProps) => {
  return makeComponentFromCommandShow(resolveCommandShow(commandShow))
}

const Key = styled.kbd`
  all: unset;
  box-sizing: border-box;
  border-radius: 4px;

  border: 1px solid var(--background-7);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
`
const KeyInner = styled.span`
  padding: 3px 6px 4px;
  font-size: 16px;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  color: var(--text-12);
`

const KeySequence = styled.span`
  display: inline-flex;
  align-items: center;
  line-height: normal;
  gap: 4px;
  flex-wrap: nowrap;
`
const KeySequenceConnector = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

const KeySequences = styled.span`
  color: var(--text-10);
  display: inline-flex;
  gap: 4px;
  line-height: normal;
  flex-wrap: wrap;
`

const KeySequencesSeparator = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`
